import {
  FileBlobWithId,
  MessageDirection,
  MessageEvent,
  PENDING_MESSAGE_PREFIX,
} from '@ucc/cc-common-ui';
import { AnyAction, Dispatch } from 'redux';
import { AppAsyncThunk, AppState } from 'state/reducer';
import { fetchWithState } from 'utils/fetch';
import { v4 as uuidv4 } from 'uuid';

import { addEvent, failMessageEvent, removeEvent } from '.';
import { MessageBody } from './types';

export const send = async (
  message: Omit<MessageBody, 'participantExternalKey'>,
  dispatch: Dispatch<AnyAction>,
  getState: () => AppState
) => {
  const id = uuidv4();
  const pendingMessage: MessageEvent = {
    id: PENDING_MESSAGE_PREFIX + id,
    direction: MessageDirection.OUTBOUND,
    conversationId: id,
    createdAt: new Date().toISOString(),
    body: message.body,
    filedIds: message.fileBlobsWithId.map(({ id }) => id),
  };

  dispatch(addEvent(pendingMessage));

  const formData = new FormData();

  formData.append(
    'message',
    new Blob(
      [
        JSON.stringify({
          body: message.body,
          correlationId: message.correlationId,
          participantExternalKey: getState().notifications?.participant?.key,
        }),
      ],
      { type: 'application/json' }
    )
  );
  message.fileBlobsWithId?.forEach(({ file }) => {
    formData.append('file', file);
  });
  if (getState().notifications?.participant?.key) {
    const response = await fetchWithState(dispatch, getState)('messages', {
      method: 'POST',
      body: formData,
    });
    if (!response) {
      dispatch(failMessageEvent(pendingMessage));
    }
  } else {
    // missing participant key, fail the message
    dispatch(failMessageEvent(pendingMessage));
  }
};

export const sendMessage =
  (message: Omit<MessageBody, 'participantExternalKey'>): AppAsyncThunk =>
  async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
    send(message, dispatch, getState);
  };

export const retryMessage =
  (messageEvent: MessageEvent, fileBlobsWithId: FileBlobWithId[]) => async (dispatch, getState) => {
    dispatch(removeEvent(messageEvent));
    send(
      {
        body: messageEvent.body,
        fileBlobsWithId: fileBlobsWithId,
      },
      dispatch,
      getState
    );
  };

export const sendChatClosed = (): AppAsyncThunk => async (dispatch, getState) => {
  if (getState().notifications?.participant?.key) {
    await fetchWithState(dispatch, getState)('participant-left', {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify({
        participantExternalKey: getState().notifications?.participant?.key,
      }),
      headers: { 'Content-Type': 'application/json' },
    });
  }
};

export const participantTypingIndicatorEvent =
  (isTyping: boolean): AppAsyncThunk =>
  async (dispatch, getState) => {
    const participantExternalKey = getState().notifications?.participant?.key;
    if (participantExternalKey) {
      const route = 'typing-indicators';

      await fetchWithState(dispatch, getState)(route, {
        method: 'POST',
        body: JSON.stringify({
          status: isTyping ? 'TYPING_ON' : 'TYPING_OFF',
          participantExternalKey,
        }),
        headers: { 'content-type': 'application/json' },
      });
    }
  };

export const participantMessageReadEvent = (): AppAsyncThunk => async (dispatch, getState) => {
  const participantExternalKey = getState().notifications?.participant?.key;
  if (participantExternalKey) {
    const route = 'read-receipts';

    await fetchWithState(dispatch, getState)(route, {
      method: 'POST',
      body: JSON.stringify({ participantExternalKey }),
      headers: { 'content-type': 'application/json' },
    });
  }
};
